// =====================================================================
// SendDocumentDialog (S64 / feature #6) — envia um documento emitido pro
// paciente por WhatsApp (reusa o nº conectado da clínica) ou Email.
// 🔒 LGPD: o destino vem pré-preenchido do cadastro do paciente, mas é
// CONFIRMÁVEL/editável — a médica confere o número/e-mail antes de enviar.
// =====================================================================
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  CircularProgress
} from "@material-ui/core";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import EmailIcon from "@material-ui/icons/Email";
import { toast } from "react-toastify";

import { sendEmittedDocument, sendDocErrorMessage } from "../../services/fileService";
import useIsMobile from "../../utils/useIsMobile";

const SendDocumentDialog = ({ open, onClose, doc, defaultPhone, defaultEmail, onSent }) => {
  const isMobile = useIsMobile();
  const [channel, setChannel] = useState("whatsapp");
  const [to, setTo] = useState("");
  const [caption, setCaption] = useState("");
  const [sending, setSending] = useState(false);

  // Ao abrir / trocar de canal, pré-preenche o destino com o dado do paciente.
  useEffect(() => {
    if (!open) return;
    setTo(channel === "whatsapp" ? defaultPhone || "" : defaultEmail || "");
  }, [open, channel, defaultPhone, defaultEmail]);

  // Reset ao fechar.
  useEffect(() => {
    if (!open) {
      setChannel("whatsapp");
      setCaption("");
      setSending(false);
    }
  }, [open]);

  const docTitle =
    (doc && doc.metadata && doc.metadata.title) ||
    (doc && doc.documentType) ||
    "documento";

  const handleSend = async () => {
    if (!to.trim()) {
      toast.warn(channel === "whatsapp" ? "Informe o número de WhatsApp." : "Informe o e-mail.");
      return;
    }
    setSending(true);
    try {
      const res = await sendEmittedDocument(doc.id, {
        channel,
        to: to.trim(),
        caption: caption.trim() || undefined
      });
      toast.success(
        res.channel === "email"
          ? `Documento enviado por e-mail para ${res.to}.`
          : `Documento enviado pelo WhatsApp para ${res.to}.`
      );
      if (onSent) onSent(res);
      onClose();
    } catch (err) {
      toast.error(sendDocErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => (!sending ? onClose() : null)} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle>Enviar documento ao paciente</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          Enviando: <b>{docTitle}</b>
        </Typography>

        <RadioGroup
          row
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          style={{ marginBottom: 4 }}
        >
          <FormControlLabel
            value="whatsapp"
            control={<Radio color="primary" />}
            label={
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <WhatsAppIcon fontSize="small" style={{ color: "#25D366" }} /> WhatsApp
              </span>
            }
          />
          <FormControlLabel
            value="email"
            control={<Radio color="primary" />}
            label={
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <EmailIcon fontSize="small" style={{ color: "#5b6b8c" }} /> E-mail
              </span>
            }
          />
        </RadioGroup>

        <TextField
          label={channel === "whatsapp" ? "WhatsApp do paciente (com DDD)" : "E-mail do paciente"}
          value={to}
          onChange={(e) => setTo(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
          margin="dense"
          placeholder={channel === "whatsapp" ? "(81) 99999-9999" : "paciente@email.com"}
          helperText={
            channel === "whatsapp"
              ? "Inclua o 9 após o DDD (ex.: (81) 9 9999-9999). O número precisa ter WhatsApp ativo."
              : undefined
          }
          autoFocus
        />

        <TextField
          label="Mensagem (opcional)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          variant="outlined"
          size="small"
          margin="dense"
        />

        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8 }}>
          ⚠️ Confira o destino acima — o documento será enviado direto pro paciente.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancelar
        </Button>
        <Button
          onClick={handleSend}
          color="primary"
          variant="contained"
          disabled={sending || !to.trim()}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {sending ? "Enviando..." : "Enviar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendDocumentDialog;
